// C-5, C-5b, C-6, C-7: the candidate taking the assessment.
//
// A lesson-style view, one capability at a time: the Roleplay, then the Quiz
// Question, with a clear progress indicator. Four things here are requirements
// rather than choices, and each is commented where it happens:
//
//   THREE ATTEMPTS, BEST LEVEL COUNTS (PRD 7.1, DA-5062 F6). A question about
//   the situation does not use one, and the server decides which a message was,
//   so the counter follows its answer rather than guessing.
//
//   CHECK THE TRANSCRIPT BEFORE SENDING (C-5b). Speaking dictates INTO the
//   answer box and never sends by itself. The candidate reads what was heard,
//   fixes it if the transcription got their accent wrong, and then sends. That
//   is what stops a transcription error being graded as a weak answer.
//
//   A TEXT ROUTE IS ALWAYS THERE, and there is NO TIME LIMIT (H-7, L-5). Voice
//   cannot be required; it is a default the candidate can leave at any point.
//
//   NO RESULTS (C-8). Nothing here shows a level, and Elliott's replies are
//   written not to reveal one.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { LuCheck, LuMic, LuSquare, LuVolume2 } from 'react-icons/lu';
import dosenLogo from '../../assets/images/brand/dosen/white-logo.svg';
import { hiringApi, hiringMessage, type CandidateMeView } from '../../api/hiring';
import ElliottMessage from '../new-designs/tabs/home/elliott-lesson/components/ElliottMessage';
import LearnerMessage from '../new-designs/tabs/home/elliott-lesson/components/LearnerMessage';
import TypingDots from '../new-designs/tabs/home/elliott-lesson/components/TypingDots';
import RoleplayBrief, { spokenBrief } from './RoleplayBrief';
import { elliottTokens as t } from '../new-designs/tabs/home/elliott-lesson/tokens';
import { useVoiceConversation } from '../new-designs/tabs/home/elliott-lesson/voice/useVoiceConversation';
import { isVoiceConversationSupported } from '../new-designs/tabs/home/elliott-lesson/voice/voiceEngine';
import {
  ActionButton,
  BODY,
  BRANDY,
  BRANDY_FILL,
  BRANDY_TINT,
  DANGER,
  DONE,
  FONT,
  LABEL,
  LINE,
  LINE_2,
  SURFACE,
  TEXT,
  TEXT_2,
  TEXT_3,
  TEXT_4,
  TEXTAREA_SX,
  WELL
} from '../new-designs/tabs/capability-setup/designSystem';

/** How many attempts are left, at a glance. */
const AttemptDots: React.FC<{ left: number; of: number }> = ({ left, of }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }} aria-hidden>
    {Array.from({ length: of }).map((_, index) => (
      <Box
        key={index}
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: index < left ? BRANDY : TEXT_4,
          transition: 'background-color .2s ease'
        }}
      />
    ))}
  </Box>
);

/** A quiet control in the answer console: Play again, and anything like it. */
const ConsoleButton: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ onClick, disabled, icon, children }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      px: '12px',
      height: 30,
      borderRadius: '15px',
      bgcolor: 'transparent',
      border: `1px solid ${LINE_2}`,
      color: disabled ? TEXT_4 : TEXT_2,
      fontFamily: FONT,
      fontSize: 12,
      fontWeight: 600,
      cursor: disabled ? 'default' : 'pointer',
      transition: 'border-color .15s ease, color .15s ease',
      '&:hover': disabled ? {} : { borderColor: BRANDY, color: BRANDY }
    }}
  >
    {icon}
    {children}
  </Box>
);

/**
 * The microphone. It is a button and only a button: pressing it starts the
 * recording and pressing it again ends it (C-5b, H-7). While it is listening
 * it says so loudly, because a live microphone nobody noticed is the one
 * thing this screen must never have.
 */
const MicButton: React.FC<{ listening: boolean; disabled?: boolean; onClick: () => void }> = ({
  listening,
  disabled,
  onClick
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={listening}
    aria-label={listening ? 'Stop recording' : 'Speak your answer'}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      px: '16px',
      height: 40,
      borderRadius: '20px',
      bgcolor: listening ? BRANDY_FILL : WELL,
      border: `1px solid ${listening ? BRANDY : LINE_2}`,
      color: listening ? BRANDY : TEXT_2,
      fontFamily: FONT,
      fontSize: 13,
      fontWeight: 600,
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'all .15s ease',
      '&:hover': disabled ? {} : { borderColor: BRANDY, color: BRANDY },
      ...(listening
        ? {
            animation: 'hiringMicPulse 1.6s ease-in-out infinite',
            '@keyframes hiringMicPulse': {
              '0%, 100%': { boxShadow: `0 0 0 0 ${BRANDY_TINT}` },
              '50%': { boxShadow: `0 0 0 6px ${BRANDY_TINT}` }
            }
          }
        : {})
    }}
  >
    {listening ? <LuSquare size={13} /> : <LuMic size={15} />}
    {listening ? 'Stop' : 'Speak'}
  </Box>
);

/**
 * One finished capability, to read back. Their own words and Elliott's, with
 * no level and no judgement of any kind (C-8), and nothing to answer: a
 * finished Roleplay is never reopened (C-7).
 */
const ReviewItem: React.FC<{
  item: CandidateMeView['items'][number];
  open: boolean;
  onToggle: (capabilityId: string) => void;
}> = ({ item, open, onToggle }) => (
  <Box sx={{ border: `1px solid ${LINE}`, borderRadius: '8px', bgcolor: WELL }}>
    <Box
      component="button"
      type="button"
      onClick={() => onToggle(item.capabilityId)}
      aria-expanded={open}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        p: '12px 14px',
        bgcolor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        color: TEXT_2,
        fontFamily: FONT,
        fontSize: 13
      }}
    >
      <LuCheck size={14} color={DONE} />
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
        {item.capabilityName}
      </Box>
      <Box component="span" sx={{ color: TEXT_3, fontSize: 12 }}>
        {open ? 'Hide' : 'Read what I wrote'}
      </Box>
    </Box>
    {open && (
      <Box sx={{ px: '14px', pb: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {item.situation && <RoleplayBrief text={item.situation} textSx={{ ...BODY, fontSize: 13, color: TEXT_3 }} />}
        {item.exchanges.map((exchange, index) =>
          exchange.from === 'candidate' ? (
            <LearnerMessage key={index}>{exchange.text}</LearnerMessage>
          ) : (
            <ElliottMessage key={index} stacked>
              <Typography sx={{ fontFamily: FONT, fontSize: 13.5, lineHeight: 1.6, color: t.textPrimary }}>
                {exchange.text}
              </Typography>
            </ElliottMessage>
          )
        )}
      </Box>
    )}
  </Box>
);

const CandidateAssessment: React.FC<{ me: CandidateMeView; onChanged: () => void }> = ({ me, onChanged }) => {
  // The first capability that is not finished is the one in hand. Resuming is
  // therefore free: the server's answers decide where they are (C-6).
  const current = useMemo(() => me.items.find((item) => !item.roleplayDone || !item.quizDone), [me.items]);
  /** The last thing Elliott said here: what Play again reads, and what a
      spoken round reads by itself when the turn changes. */
  const lastSpoken = useMemo(() => {
    const said = [...(current?.exchanges || [])].reverse().find((exchange) => exchange.from === 'elliott');
    return said ? said.text : spokenBrief(current?.situation || '');
  }, [current?.exchanges, current?.situation]);
  const readsAloud = me.defaultInputMode === 'voice';
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voiceDenied, setVoiceDenied] = useState(false);
  const canSpeak = isVoiceConversationSupported();
  /** How they actually produced this answer, for the transcript (H-23). */
  const usedVoice = useRef(false);
  const [sent, setSent] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);
  const [reviewing, setReviewing] = useState<string | null>(null);
  const toggleReview = (capabilityId: string) =>
    setReviewing((openId) => (openId === capabilityId ? null : capabilityId));
  // H-23: the response time runs from when Elliott finished presenting to when
  // the candidate submits. Context for the reviewer only (L-6).
  const presentedAt = useRef<number>(Date.now());
  // H-25, information only and never scored.
  const pasteCount = useRef(0);
  const tabSwitches = useRef(0);
  const copyCount = useRef(0);
  const draftRef = useRef('');

  const setDraftNow = (text: string) => {
    draftRef.current = text;
    setDraft(text);
  };

  useEffect(() => {
    presentedAt.current = Date.now();
    setDraftNow('');
    setSent(null);
    usedVoice.current = false;
    pasteCount.current = 0;
    copyCount.current = 0;
    // Reset with the other two: it was left running for the whole item, so
    // the last answer carried every departure since the Roleplay opened and
    // read as ten when it was one. All three now mean the same thing —
    // "while writing THIS answer".
    tabSwitches.current = 0;
  }, [current?.capabilityId, current?.exchanges.length]);

  // Always on the newest message: a reply that lands below the fold, in a
  // box the candidate has to go looking for, may as well not have arrived.
  useEffect(() => {
    const box = scroller.current;
    if (!box) return;
    box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' });
  }, [current?.capabilityId, current?.exchanges.length, current?.roleplayDone, sent, sending]);

  useEffect(() => {
    // Hiding the tab and switching to another application are the same act to
    // the person doing it, and only the first fires `visibilitychange`:
    // Cmd-Tab to another app leaves the tab visible, so a whole way of
    // leaving the page went uncounted. `away` keeps one departure from being
    // counted twice when both events fire.
    let away = false;
    const left = () => {
      if (away) return;
      away = true;
      tabSwitches.current += 1;
    };
    const back = () => {
      away = false;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') left();
      else back();
    };
    // Copying the situation out of the page, to paste it somewhere else. It
    // is counted and shown to the hiring manager as context, in the same
    // breath as the other two and under the same caption: it is not proof of
    // anything on its own, and it is never part of a level (H-25, L-6).
    //
    // A screenshot cannot be detected at all — no browser reports one — so
    // nothing here pretends to.
    const onCopy = () => {
      copyCount.current += 1;
    };
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('copy', onCopy);
    window.addEventListener('blur', left);
    window.addEventListener('focus', back);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('copy', onCopy);
      window.removeEventListener('blur', left);
      window.removeEventListener('focus', back);
    };
  }, []);

  /**
   * Speaking fills the answer box and stops. It never sends: C-5b is the whole
   * point of this screen, and an auto-send would take the check away.
   */
  const voice = useVoiceConversation({
    // Always on where the browser supports it, so Elliott can read and Play
    // again works in either mode. It is the INPUT MODE below that decides
    // whether the microphone ever opens, and only a press opens it.
    active: canSpeak,
    onTranscript: (text) => {
      const kept = draftRef.current.trimEnd();
      setDraftNow(kept ? `${kept} ${text}` : text);
    },
    onReadyToSend: () => false,
    onMicDenied: () => {
      setVoiceDenied(true);
    },
    /**
     * NOTHING is given to the transcriber as context here, and that is a rule,
     * not a tuning choice.
     *
     * The transcription model takes this string as a `prompt`. In a lesson
     * that biases it toward the scenario's vocabulary and cuts hallucinations
     * on short clips. In an assessment it does the opposite: the situation
     * ends in a direct instruction ("Talk through how you would..."), so a
     * clip of a quiet room reads to the model as the start of an answer and it
     * WRITES ONE — fluent, on topic, and in the candidate's answer box.
     *
     * That happened on 26 Oct 2026, with the microphone simply left open: a
     * full, competent answer appeared that the candidate had never said. One
     * Send away from being graded as their own words, on a level that decides
     * whether they are hired.
     *
     * The server refuses the prompt for a candidate as well, so this cannot be
     * undone from here alone (BE elliottVoiceStt).
     */
    getSttContext: () => ''
  });

  /**
   * Elliott READS each new turn aloud when the round is a spoken one: the
   * situation as the Roleplay opens, and every reply after it. Until 26 Oct
   * 2026 nothing on this screen ever spoke, so a round whose own default was
   * voice opened in silence.
   *
   * Reading is all it does. The microphone is a button (C-5b, H-7): it opens
   * when the candidate presses it and at no other moment.
   */
  const spokenRef = useRef('');
  useEffect(() => {
    if (!canSpeak || !readsAloud || !current) return;
    const turn = `${current.capabilityId}:${(current.exchanges || []).length}`;
    if (!lastSpoken.trim() || spokenRef.current === turn) return;
    spokenRef.current = turn;
    voice.speak(lastSpoken);
    // `voice` is rebuilt every render; the turn key is what decides, not it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSpeak, readsAloud, current?.capabilityId, current?.exchanges.length, lastSpoken]);

  const sendRoleplay = async () => {
    const response = draftRef.current.trim();
    if (!response || !current || sending) return;
    setSending(true);
    setError(null);
    // Their words go up straight away. Elliott takes a few seconds, and until
    // 26 Oct 2026 the answer simply vanished from the screen for all of them:
    // the box emptied, nothing appeared, and the only way to know it had been
    // sent was to wait and see.
    setSent(response);
    try {
      await hiringApi.answerRoleplay(current.capabilityId, {
        response,
        inputMode: usedVoice.current ? 'voice' : 'text',
        presentedAt: presentedAt.current,
        pasteCount: pasteCount.current,
        tabSwitches: tabSwitches.current,
        copyCount: copyCount.current
      });
      setDraftNow('');
      onChanged();
    } catch (err) {
      setSent(null);
      setError(hiringMessage(err, 'Your answer could not be sent. Please try again.'));
    } finally {
      setSending(false);
    }
  };

  const answerQuiz = async (index: number) => {
    if (!current || sending) return;
    setSending(true);
    setError(null);
    try {
      await hiringApi.answerQuiz(current.capabilityId, index);
      onChanged();
    } catch (err) {
      setError(hiringMessage(err, 'Your answer could not be saved. Please choose it again.'));
    } finally {
      setSending(false);
    }
  };

  if (!current) return null;
  const onQuiz = current.roleplayDone && !current.quizDone;
  const listening = voice.state === 'listening';
  const reading = voice.state === 'speaking' || voice.state === 'preparing';
  const replay = () => voice.speak(lastSpoken);
  const toggleSpeaking = () => {
    if (listening) {
      voice.toggleMic();
      return;
    }
    setVoiceDenied(false);
    usedVoice.current = true;
    voice.setInputMode('speak');
    voice.listen();
  };
  const reviewable = me.items.filter(
    (item) => item.capabilityId !== current.capabilityId && (item.exchanges || []).length > 0
  );
  const attemptsLeft = current.attemptsAllowed - current.attemptsUsed;
  const done = me.items.filter((i) => i.roleplayDone && i.quizDone).length;

  return (
    <Box
      sx={{
        // A fixed height, not a minimum: the header and the answer console
        // stay put and the conversation is the only thing that scrolls. With
        // a minimum the whole page grew instead, so a long exchange pushed
        // the answer box off the bottom of the window and the candidate had
        // to scroll the page back to find it.
        height: '100vh',
        overflow: 'hidden',
        bgcolor: '#0F0F0F',
        display: 'flex',
        flexDirection: 'column',
        // The app has no global border-box reset (index.css sets it on `body`
        // alone, and quotes the value, so the browser drops the declaration).
        // Inside the product's Layout, MUI's own components hide that. These
        // screens live outside it, so a padded full-width row was 40px wider
        // than the window and the logo was cut off the right edge.
        boxSizing: 'border-box',
        '& *, & *::before, & *::after': { boxSizing: 'border-box' }
      }}
    >
      {/* The header carries the progress and nothing else: there is nowhere else to go (C-9). */}
      <Box
        sx={{
          px: '20px',
          py: '14px',
          borderBottom: `1px solid ${LINE}`,
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography sx={{ ...LABEL, color: TEXT_3 }}>
            {me.orgName} · {me.roleName}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: TEXT, mt: '2px' }}>
            {current.capabilityName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {me.items.map((item) => {
            const finished = item.roleplayDone && item.quizDone;
            const here = item.capabilityId === current.capabilityId;
            return (
              <Box
                key={item.capabilityId}
                aria-label={`${item.capabilityName}: ${finished ? 'done' : here ? 'in progress' : 'to come'}`}
                sx={{
                  width: here ? 22 : 8,
                  height: 8,
                  borderRadius: '4px',
                  bgcolor: finished ? DONE : here ? BRANDY : TEXT_4
                }}
              />
            );
          })}
          <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_3, ml: '4px' }}>
            {done} of {me.items.length} done
          </Typography>
        </Box>
        <Box component="img" src={dosenLogo} alt="Dosen" sx={{ height: 18, opacity: 0.5, flexShrink: 0 }} />
      </Box>

      <Box
        ref={scroller}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: '20px',
          py: '24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '14px'
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 760,
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
          }}
        >
          {/* L-2: Elliott is named as an AI where the candidate meets it. */}
          <ElliottMessage stacked>
            {current.situation ? (
              <RoleplayBrief
                text={current.situation}
                textSx={{ fontFamily: FONT, fontSize: 14, lineHeight: 1.6, color: t.textPrimary }}
              />
            ) : (
              // The situation comes with the step itself, so this is only ever
              // the moment between the two. It is never an empty bubble: a
              // candidate staring at a blank Roleplay has no way to tell a
              // slow page from a broken one.
              <TypingDots />
            )}
          </ElliottMessage>

          {current.exchanges.map((exchange, index) =>
            exchange.from === 'candidate' ? (
              <LearnerMessage key={index}>{exchange.text}</LearnerMessage>
            ) : (
              <ElliottMessage key={index} stacked>
                <Typography sx={{ fontFamily: FONT, fontSize: 14, lineHeight: 1.6, color: t.textPrimary }}>
                  {exchange.text}
                </Typography>
              </ElliottMessage>
            )
          )}

          {/* What they have already answered, to read again and nothing more.
              A finished Roleplay is never reopened (C-7) and no level is ever
              shown (C-8), but their own words are theirs to re-read: without
              this the moment an item closed, everything they had written
              disappeared. */}
          {reviewable.length > 0 && (
            <Box sx={{ mt: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Typography sx={{ ...LABEL, color: TEXT_4 }}>What you have answered so far</Typography>
              {reviewable.map((item) => (
                <ReviewItem
                  key={item.capabilityId}
                  item={item}
                  open={reviewing === item.capabilityId}
                  onToggle={toggleReview}
                />
              ))}
            </Box>
          )}

          {/* Their answer is on screen before the server has it, and Elliott
              is visibly thinking: a few seconds of nothing reads as an answer
              that was lost. */}
          {sent && <LearnerMessage>{sent}</LearnerMessage>}
          {sending && (
            <ElliottMessage stacked>
              <TypingDots />
            </ElliottMessage>
          )}

          {onQuiz && current.quiz && (
            <Box
              sx={{
                bgcolor: SURFACE,
                border: `1px solid ${LINE}`,
                borderRadius: '8px',
                p: '18px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}
            >
              <Typography sx={{ ...LABEL, color: TEXT_3 }}>One question on {current.capabilityName}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 14, color: TEXT }}>{current.quiz.question}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {current.quiz.options.map((option, index) => (
                  <Box
                    key={index}
                    component="button"
                    type="button"
                    disabled={sending}
                    onClick={() => answerQuiz(index)}
                    sx={{
                      textAlign: 'left',
                      p: '12px 14px',
                      bgcolor: WELL,
                      border: `1px solid ${LINE}`,
                      borderRadius: '4px',
                      color: TEXT,
                      fontFamily: FONT,
                      fontSize: 13.5,
                      cursor: sending ? 'wait' : 'pointer',
                      '&:hover': { borderColor: BRANDY }
                    }}
                  >
                    {option}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {error && (
            <Typography sx={{ ...BODY, color: DANGER }} role="alert">
              {error}
            </Typography>
          )}
        </Box>
      </Box>

      {!onQuiz && (
        <Box sx={{ borderTop: `1px solid ${LINE}`, bgcolor: '#0C0C0C', px: '20px', py: '18px' }}>
          <Box sx={{ maxWidth: 760, mx: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <AttemptDots left={attemptsLeft} of={current.attemptsAllowed} />
              <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_2 }}>
                {`${attemptsLeft} ${attemptsLeft === 1 ? 'attempt' : 'attempts'} left`}
              </Typography>
              <Box sx={{ width: '1px', height: 14, flexShrink: 0, bgcolor: '#3A3A3A' }} />
              <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>Your best attempt is used</Typography>
              <Box sx={{ flex: 1 }} />
              {/* Hearing it again is free, and the line under the box says so.
                  Without it a candidate who missed a word had no way back to
                  it but to read, which is not much of a spoken assessment. */}
              {canSpeak && (
                <ConsoleButton onClick={replay} disabled={reading || sending} icon={<LuVolume2 size={13} />}>
                  {reading ? 'Reading…' : 'Play again'}
                </ConsoleButton>
              )}
            </Box>

            <Box
              component="textarea"
              rows={4}
              value={draft}
              disabled={sending}
              placeholder={
                listening ? 'What we hear appears here. You can edit it.' : 'Your answer. There is no time limit.'
              }
              onPaste={() => {
                pasteCount.current += 1;
              }}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraftNow(e.target.value)}
              sx={{
                ...TEXTAREA_SX,
                bgcolor: SURFACE,
                ...(listening ? { borderColor: BRANDY, boxShadow: `0 0 0 3px ${BRANDY_TINT}` } : {})
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {/* H-7, L-5: the microphone is never required, and it NEVER
                  opens by itself. It used to open the moment Elliott stopped
                  reading, which left a live microphone in front of someone
                  who had not asked for one — and a room's noise is enough for
                  a transcriber to invent words with. Speaking starts when the
                  candidate says it starts. */}
              {canSpeak && <MicButton listening={listening} disabled={sending || reading} onClick={toggleSpeaking} />}
              <Typography sx={{ ...BODY, fontSize: 12.5, color: listening ? BRANDY : TEXT_2, flex: 1, minWidth: 160 }}>
                {listening
                  ? 'Listening. What we hear appears above, and only those words are assessed.'
                  : reading
                  ? 'Elliott is reading the situation out.'
                  : canSpeak
                  ? 'Type your answer, or press the microphone to speak it.'
                  : 'Type your answer.'}
              </Typography>
              <ActionButton
                primary
                disabled={!draft.trim() || sending}
                reason={!draft.trim() ? 'Write or speak your answer first' : undefined}
                onClick={sendRoleplay}
              >
                {sending ? 'Sending...' : 'Send'}
              </ActionButton>
            </Box>

            {voiceDenied && (
              <Typography sx={{ ...BODY, fontSize: 12, color: DANGER }}>
                We couldn&apos;t use your microphone, so you can type your answer instead. There is no time limit.
              </Typography>
            )}

            <Typography sx={{ ...BODY, fontSize: 11.5, color: TEXT_4, lineHeight: 1.6 }}>
              Asking Elliott to repeat or explain the situation doesn&apos;t use an attempt. There is no time limit.
              {/* Said plainly, and before they do any of it, because it is
                  recorded either way (H-25, L-6). */}{' '}
              Pasting into your answer, copying from this page and leaving this tab are counted and shown to them as
              context but are never used as part of your assessment.
            </Typography>
          </Box>
        </Box>
      )}

      {onQuiz && (
        <Box sx={{ borderTop: `1px solid ${LINE}`, px: '20px', py: '14px' }}>
          <Box
            sx={{
              maxWidth: 760,
              mx: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: DONE,
              fontFamily: FONT,
              fontSize: 12.5
            }}
          >
            <LuCheck size={14} />
            Roleplay done. Choose an answer to the question above to move on.
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CandidateAssessment;
